import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Edit2, Trash2, Upload, AlertCircle } from 'lucide-react';
import { Button } from '../components/ui/Button';
import { Modal } from '../components/ui/Modal';
import { Input } from '../components/ui/Input';
import toast from 'react-hot-toast';
import { apiClient } from '../services/api/client';

interface Perimeter {
  id: number;
  name: string;
  displayName: string;
  description?: string;
  logo?: string;
  isActive: boolean;
  createdAt: string;
  updatedAt: string;
}

interface PerimeterFormData {
  name: string;
  displayName: string;
  description: string;
  isActive: boolean;
}

export default function Perimeters() {
  const queryClient = useQueryClient();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPerimeter, setEditingPerimeter] = useState<Perimeter | null>(null);
  const [formData, setFormData] = useState<PerimeterFormData>({
    name: '',
    displayName: '',
    description: '',
    isActive: true,
  });
  const [logoFile, setLogoFile] = useState<File | null>(null);

  // Récupérer les périmètres
  const { data: perimeters = [], isLoading } = useQuery({
    queryKey: ['perimeters'],
    queryFn: async () => {
      try {
        const data = await apiClient.get<Perimeter[]>('/perimeters');
        return data || [];
      } catch (error) {
        console.error('Erreur lors du chargement des périmètres:', error);
        return [];
      }
    },
  });

  // Créer/Modifier un périmètre
  const mutation = useMutation({
    mutationFn: async (data: PerimeterFormData & { logo?: File }) => {
      const formDataToSend = new FormData();
      formDataToSend.append('name', data.name);
      formDataToSend.append('displayName', data.displayName);
      formDataToSend.append('description', data.description);
      formDataToSend.append('isActive', String(data.isActive));
      
      if (data.logo) {
        formDataToSend.append('logo', data.logo);
      }

      if (editingPerimeter) {
        const patchPayload = {
          displayName: data.displayName,
          description: data.description,
          isActive: data.isActive,
        };
        const res = await apiClient.patch(`/perimeters/${editingPerimeter.id}`, patchPayload);
        if (data.logo) {
          await apiClient.uploadFile(`/perimeters/${editingPerimeter.id}/logo`, data.logo);
        }
        return res;
      } else {
        return apiClient.post('/perimeters', formDataToSend);
      }
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['perimeters'] });
      await queryClient.invalidateQueries({ queryKey: ['perimeters', 'list'] });
      toast.success(editingPerimeter ? 'Périmètre modifié' : 'Périmètre créé');
      resetForm();
      setIsModalOpen(false);
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erreur lors de l\'opération');
    },
  });

  // Supprimer un périmètre
  const deleteMutation = useMutation({
    mutationFn: async (id: number) => {
      return apiClient.delete(`/perimeters/${id}`);
    },
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: ['perimeters'] });
      await queryClient.invalidateQueries({ queryKey: ['perimeters', 'list'] });
      toast.success('Périmètre supprimé');
    },
    onError: (error: any) => {
      toast.error(error.response?.data?.message || 'Erreur lors de la suppression');
    },
  });

  const resetForm = () => {
    setFormData({
      name: '',
      displayName: '',
      description: '',
      isActive: true,
    });
    setLogoFile(null);
    setEditingPerimeter(null);
  };

  const handleEdit = (perimeter: Perimeter) => {
    setEditingPerimeter(perimeter);
    setFormData({
      name: perimeter.name,
      displayName: perimeter.displayName,
      description: perimeter.description || '',
      isActive: perimeter.isActive,
    });
    setIsModalOpen(true);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    mutation.mutate({
      ...formData,
      logo: logoFile || undefined,
    });
  };

  const handleOpenModal = () => {
    resetForm();
    setIsModalOpen(true);
  };

  if (isLoading) {
    return <div className="flex justify-center items-center h-screen">Chargement...</div>;
  }

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white">Gestion des Périmètres</h1>
        <Button onClick={handleOpenModal} className="flex items-center gap-2">
          <Plus size={20} />
          Nouveau Périmètre
        </Button>
      </div>

      {/* Avertissement pour SUPERADMIN */}
      <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex gap-3">
        <AlertCircle className="text-blue-600 dark:text-blue-400 flex-shrink-0" size={20} />
        <div>
          <h3 className="font-semibold text-blue-900 dark:text-blue-100">Gestion des Périmètres</h3>
          <p className="text-sm text-blue-800 dark:text-blue-200">
            Les périmètres permettent de séparer les univers clients. Chaque périmètre a son propre logo et ses propres organisations.
          </p>
        </div>
      </div>

      {/* Tableau des périmètres */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow overflow-hidden">
        <table className="w-full">
          <thead className="bg-gray-50 dark:bg-gray-700">
            <tr>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Nom</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Nom d'affichage</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Logo</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Statut</th>
              <th className="px-6 py-3 text-left text-sm font-semibold text-gray-900 dark:text-white">Actions</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-200 dark:divide-gray-700">
            {perimeters && perimeters.length > 0 ? (
              perimeters.map((perimeter: Perimeter) => (
                <tr key={perimeter.id} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{perimeter.name}</td>
                  <td className="px-6 py-4 text-sm text-gray-900 dark:text-white">{perimeter.displayName}</td>
                  <td className="px-6 py-4 text-sm">
                    {perimeter.logo ? (
                      <img src={perimeter.logo} alt="Logo" className="h-8 w-8 rounded" />
                    ) : (
                      <span className="text-gray-500 dark:text-gray-400">Aucun</span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm">
                    <span className={`px-3 py-1 rounded-full text-xs font-semibold ${
                      perimeter.isActive
                        ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400'
                        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400'
                    }`}>
                      {perimeter.isActive ? 'Actif' : 'Inactif'}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm flex gap-2">
                    <button
                      onClick={() => handleEdit(perimeter)}
                      className="text-blue-600 hover:text-blue-900 dark:text-blue-400 dark:hover:text-blue-300"
                    >
                      <Edit2 size={18} />
                    </button>
                    <button
                      onClick={() => deleteMutation.mutate(perimeter.id)}
                      className="text-red-600 hover:text-red-900 dark:text-red-400 dark:hover:text-red-300"
                    >
                      <Trash2 size={18} />
                    </button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan={5} className="px-6 py-4 text-center text-gray-500 dark:text-gray-400">
                  Aucun périmètre trouvé
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Modal de création/modification */}
      <Modal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} title={editingPerimeter ? 'Modifier le Périmètre' : 'Créer un Périmètre'}>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nom (identifiant unique)
            </label>
            <Input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              placeholder="ex: client-a"
              disabled={!!editingPerimeter}
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Nom d'affichage
            </label>
            <Input
              type="text"
              value={formData.displayName}
              onChange={(e) => setFormData({ ...formData, displayName: e.target.value })}
              placeholder="ex: Client A"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
            </label>
            <textarea
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              placeholder="Description du périmètre"
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg dark:bg-gray-700 dark:text-white"
              rows={3}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Logo
            </label>
            <div className="flex items-center gap-2">
              <input
                type="file"
                accept="image/*"
                onChange={(e) => setLogoFile(e.target.files?.[0] || null)}
                className="flex-1"
              />
              {logoFile && <span className="text-sm text-green-600 dark:text-green-400">✓ Fichier sélectionné</span>}
            </div>
          </div>

          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="isActive"
              checked={formData.isActive}
              onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
              className="rounded"
            />
            <label htmlFor="isActive" className="text-sm font-medium text-gray-700 dark:text-gray-300">
              Actif
            </label>
          </div>

          <div className="flex gap-2 justify-end pt-4">
            <Button
              type="button"
              onClick={() => setIsModalOpen(false)}
              className="bg-gray-300 hover:bg-gray-400 text-gray-900"
            >
              Annuler
            </Button>
            <Button type="submit" disabled={mutation.isPending}>
              {mutation.isPending ? 'Enregistrement...' : 'Enregistrer'}
            </Button>
          </div>
        </form>
      </Modal>
    </div>
  );
}
